import { isSupabaseAuthConfigured, supabase } from "./lib/supabaseClient";
import { PUBLIC_CONFIG } from "./lib/publicConfig";

const ACCESS_TOKEN_KEY = "justtrackit.access_token";
const REFRESH_TOKEN_KEY = "justtrackit.refresh_token";
const AUTH_REFRESH_ENDPOINT = PUBLIC_CONFIG.AUTH_REFRESH_ENDPOINT;

function readStorage(key) {
  if (typeof window === "undefined") return null;
  const localValue = window.localStorage.getItem(key);
  if (localValue) return localValue;
  return window.sessionStorage.getItem(key);
}

function writeStorage(key, value, persist) {
  if (typeof window === "undefined") return;
  const target = persist ? window.localStorage : window.sessionStorage;
  const other = persist ? window.sessionStorage : window.localStorage;
  target.setItem(key, value);
  other.removeItem(key);
}

function removeStorage(key) {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(key);
  window.sessionStorage.removeItem(key);
}

export function getStoredAccessToken() {
  return readStorage(ACCESS_TOKEN_KEY);
}

export function getStoredRefreshToken() {
  return readStorage(REFRESH_TOKEN_KEY);
}

export function storeSessionTokens({ accessToken, refreshToken, persist = true } = {}) {
  if (accessToken) writeStorage(ACCESS_TOKEN_KEY, String(accessToken), persist);
  if (refreshToken) writeStorage(REFRESH_TOKEN_KEY, String(refreshToken), persist);
}

export function clearSessionTokens() {
  removeStorage(ACCESS_TOKEN_KEY);
  removeStorage(REFRESH_TOKEN_KEY);
}

export async function refreshSessionAccessToken() {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) {
    return null;
  }

  if (!AUTH_REFRESH_ENDPOINT && isSupabaseAuthConfigured && supabase) {
    const { data, error } = await supabase.auth.refreshSession({
      refresh_token: refreshToken
    });

    const nextAccessToken = String(data?.session?.access_token || "").trim();
    const nextRefreshToken = String(data?.session?.refresh_token || refreshToken).trim();

    if (error || !nextAccessToken) {
      clearSessionTokens();
      return null;
    }

    storeSessionTokens({
      accessToken: nextAccessToken,
      refreshToken: nextRefreshToken,
      persist: true
    });

    return nextAccessToken;
  }

  if (!AUTH_REFRESH_ENDPOINT) {
    return null;
  }

  const response = await fetch(AUTH_REFRESH_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    },
    body: JSON.stringify({ refreshToken })
  });

  if (!response.ok) {
    clearSessionTokens();
    return null;
  }

  const data = await response.json();
  const accessToken = String(data?.accessToken || "").trim();
  const nextRefreshToken = String(data?.refreshToken || refreshToken).trim();

  if (!accessToken) {
    clearSessionTokens();
    return null;
  }

  storeSessionTokens({
    accessToken,
    refreshToken: nextRefreshToken,
    persist: true
  });

  return accessToken;
}
